import * as React from "react";
import { cn } from "@/lib/utils";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    hasError?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, hasError, placeholder, onFocus: propsOnFocus, onBlur: propsOnBlur, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    // Merge refs
    const handleRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
        textareaRef.current = node;
      },
      [ref]
    );

    const handleFocus = React.useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);

      // Select all text if there's a value
      if (e.target.value) {
        e.target.select();
      }

      // Call original onFocus if provided
      if (propsOnFocus) {
        propsOnFocus(e);
      }
    }, [propsOnFocus]);

    const handleBlur = React.useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);

      // Call original onBlur if provided
      if (propsOnBlur) {
        propsOnBlur(e);
      }
    }, [propsOnBlur]);

    return (
      <textarea
        className={cn(
          // Base styles with form theme variables
          // Mobile: larger padding and text for touch-friendly targets
          "min-h-[80px] md:min-h-[80px] w-full rounded-none px-4 py-3 md:px-3 md:py-2 text-base md:text-sm",
          "bg-form border border-form-border text-form-text placeholder:text-form-placeholder",
          // Simplified mobile: minimal transitions, no shadows
          "transition-colors duration-150",
          // Focus state - clean, minimal
          "focus:ring-1 focus:ring-form-border-focus focus:outline-none focus:bg-form-focus",
          "disabled:cursor-not-allowed disabled:opacity-50",
          hasError && "border-form-error focus:ring-form-error",
          className
        )}
        placeholder={placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
        ref={handleRef}
      />
    );
  }
);

Textarea.displayName = "Textarea";

export { Textarea };
